import type {
  Bar,
  DatafeedConfiguration,
  DatafeedErrorCallback,
  HistoryCallback,
  IBasicDataFeed,
  LibrarySymbolInfo,
  OnReadyCallback,
  ResolutionString,
  ResolveCallback,
  SearchSymbolResultItem,
  SubscribeBarsCallback,
} from "@ekidenfi/tradingview-chart";
import type { Interval, KlineEventHandler, KlineSnapshot } from "@ekidenfi/ts-sdk";

import { getEkidenSdkClient, isMockMode } from "@src/api/sdk";

const RESOLUTION_MAP: Record<string, string> = {
  "1": "1m",
  "3": "3m",
  "5": "5m",
  "15": "15m",
  "30": "30m",
  "60": "1h",
  "120": "2h",
  "240": "4h",
  "360": "6h",
  "720": "12h",
  D: "1d",
  "1D": "1d",
  W: "1w",
  "1W": "1w",
  M: "1M",
  "1M": "1M",
};

const WS_INTERVAL_MAP: Record<string, string> = {
  "1": "1m",
  "3": "3m",
  "5": "5m",
  "15": "15m",
  "30": "30m",
  "60": "1h",
  "120": "2h",
  "240": "4h",
  "360": "6h",
  "720": "12h",
  D: "1d",
  "1D": "1d",
  "3D": "3d",
  W: "1w",
  "1W": "1w",
  M: "1M",
  "1M": "1M",
};

const SUPPORTED_RESOLUTIONS = [
  "1",
  "3",
  "5",
  "15",
  "30",
  "60",
  "120",
  "240",
  "360",
  "720",
  "1D",
  "3D",
  "1W",
  "1M",
] as ResolutionString[];

const DEFAULT_CONFIG: DatafeedConfiguration = {
  supported_resolutions: SUPPORTED_RESOLUTIONS,
  exchanges: [{ value: "Ekiden", name: "Ekiden", desc: "Ekiden Perpetuals" }],
  symbols_types: [{ name: "crypto", value: "crypto" }],
};

const getPriceScale = (symbol: string): number => {
  if (symbol.startsWith("BTC")) return 100;
  if (symbol.startsWith("ETH")) return 100;
  if (symbol.startsWith("SOL")) return 1000;
  return 10000;
};

const klineToBar = (kline: KlineSnapshot): Bar => ({
  time: Number(kline.t),
  open: Number(kline.o),
  high: Number(kline.h),
  low: Number(kline.l),
  close: Number(kline.c),
  volume: Number(kline.v) || 0,
});

export class EkidenDatafeed implements IBasicDataFeed {
  private lastBarCache = new Map<string, Bar>();
  private subscriptions = new Map<string, () => void>();
  private pollingIntervals = new Map<string, NodeJS.Timeout>();
  private symbols: string[];

  constructor(symbols: string[] = []) {
    this.symbols = symbols.length > 0 ? symbols : ["BTCUSDT", "ETHUSDT", "APTUSDT", "SOLUSDT"];
  }

  onReady(callback: OnReadyCallback): void {
    setTimeout(() => callback(DEFAULT_CONFIG));
  }

  searchSymbols(
    _userInput: string,
    _exchange: string,
    _symbolType: string,
    onResult: (result: SearchSymbolResultItem[]) => void,
  ): void {
    onResult([]);
  }

  resolveSymbol(symbolName: string, onResolve: ResolveCallback, onError: DatafeedErrorCallback): void {
    const symbol = symbolName.split(":").pop() || symbolName;

    if (!this.symbols.includes(symbol)) {
      onError(`Symbol not found: ${symbol}`);
      return;
    }

    const symbolInfo: LibrarySymbolInfo = {
      ticker: symbol,
      name: symbol,
      description: symbol.replace("-USDC", "/USDC"),
      type: "crypto",
      session: "24x7",
      timezone: "Etc/UTC",
      exchange: "Ekiden",
      listed_exchange: "Ekiden",
      format: "price",
      minmov: 1,
      pricescale: getPriceScale(symbol),
      has_intraday: true,
      has_weekly_and_monthly: true,
      supported_resolutions: SUPPORTED_RESOLUTIONS,
      volume_precision: 2,
      data_status: "streaming",
    };

    setTimeout(() => onResolve(symbolInfo));
  }

  async getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: { from: number; to: number; countBack?: number; firstDataRequest?: boolean },
    onResult: HistoryCallback,
    onError: DatafeedErrorCallback,
  ): Promise<void> {
    const { from, to, firstDataRequest } = periodParams;
    const symbol = symbolInfo.ticker || symbolInfo.name;
    const interval = RESOLUTION_MAP[resolution] || "1h";

    try {
      const response = await getEkidenSdkClient().market.getKline({
        symbol,
        interval: interval as Interval,
        limit: 1000,
        start_time: String(from * 1000),
        end_time: String(to * 1000),
      });

      if (!response.list?.length) {
        onResult([], { noData: true });
        return;
      }

      const bars = response.list.map(klineToBar).sort((a: Bar, b: Bar) => a.time - b.time);

      if (firstDataRequest && bars.length > 0) {
        this.lastBarCache.set(symbol, bars[bars.length - 1]);
      }

      onResult(bars, { noData: false });
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
    }
  }

  subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onTick: SubscribeBarsCallback,
    listenerGuid: string,
  ): void {
    const symbol = symbolInfo.ticker || symbolInfo.name;
    const wsInterval = WS_INTERVAL_MAP[resolution] || "1";
    const restInterval = RESOLUTION_MAP[resolution] || "1m";
    const ekidenSdkClient = getEkidenSdkClient();

    if (!isMockMode && ekidenSdkClient.publicStream) {
      const handler: KlineEventHandler = (msg) => {
        if (msg.op === "event" && msg.data) {
          const kline = msg.data;
          const bar: Bar = {
            time: Number(kline.t),
            open: Number(kline.o),
            high: Number(kline.h),
            low: Number(kline.l),
            close: Number(kline.c),
            volume: Number(kline.v) || 0,
          };

          const lastBar = this.lastBarCache.get(symbol);

          if (!lastBar || bar.time >= lastBar.time) {
            this.lastBarCache.set(symbol, bar);
            onTick(bar);
          }
        }
      };

      const unsubscribe = ekidenSdkClient.publicStream.subscribeKline(symbol, wsInterval, handler);
      this.subscriptions.set(listenerGuid, unsubscribe);
      return;
    }

    const poll = async () => {
      try {
        const response = await ekidenSdkClient.market.getKline({
          symbol,
          interval: restInterval as Interval,
          limit: 1,
        });

        if (response.list?.length) {
          const bar = klineToBar(response.list[0]);
          const lastBar = this.lastBarCache.get(symbol);

          if (!lastBar || bar.time >= lastBar.time) {
            this.lastBarCache.set(symbol, bar);
            onTick(bar);
          }
        }
      } catch {}
    };

    const intervalId = setInterval(poll, 5000);
    this.pollingIntervals.set(listenerGuid, intervalId);
  }

  unsubscribeBars(listenerGuid: string): void {
    const unsubscribe = this.subscriptions.get(listenerGuid);
    if (unsubscribe) {
      unsubscribe();
      this.subscriptions.delete(listenerGuid);
    }

    const intervalId = this.pollingIntervals.get(listenerGuid);
    if (intervalId) {
      clearInterval(intervalId);
      this.pollingIntervals.delete(listenerGuid);
    }
  }
}
